/* eslint-disable @typescript-eslint/naming-convention */
import { Action, ActionCreator, createAction, props } from '@ngrx/store';
import { RestApiError } from 'src/app/common-components/errors/models/error-message';
import { ReportDownloadTypes } from '../models/report-download-types';
import { Reports } from '../reports.enum';

export type ReportDashboardPageEnterActionProps = {
   allowExcel?: boolean;
   allowHtml?: boolean;
   allowPdf?: boolean;
   allowMemorize?: boolean;
   name: string;
   id: Reports;
};

export type ReportPageEnterActionProps = {
   filename: string;
   name: string;
   url: string;
   saveUrl?: string;
   allowExcel?: boolean;
   allowHtml?: boolean;
   allowPdf?: boolean;
   allowMemorize?: boolean;
   memorizedReportId?: number;
   contextHelpIds?: number[];
};

type NewType = Reports;

export interface ReportPageEnterDrilldownActionProps {
   baseDrilldownReport: string;
   id: NewType;
   isParsed: boolean;
   reportParameters: any;
}

export interface ReportErrorProp {
   error: RestApiError;
}
export interface ReportDownloadResultProp {
   downloadType: ReportDownloadTypes;
}
export interface ReportParametersProp<P = any> {
   rawFormValue: any;
   reportParameters: P;
}
export interface ReportDataProp<D = any> {
   reportData: D;
}
export interface ReportViewModeChangedActionProps<V = any> {
   viewMode: V | null;
}

export interface ReportParametersSaveProp {
   name: string;
}

export interface CommonReportActionProps {
   id: Reports;
}

export type ReportActionCreator<T extends string> = ActionCreator<
   T,
   (props: CommonReportActionProps) => CommonReportActionProps & Action<T>
>;
export type ReportActionCreatorWithProps<T extends string, P> = ActionCreator<
   T,
   (props: P & CommonReportActionProps) => P & CommonReportActionProps & Action<T>
>;

export type PageEnterActionCreator = ReportActionCreatorWithProps<
   ReportActionTypes.PageEnter,
   ReportPageEnterActionProps
>;
export type PageEnterAction = ReturnType<PageEnterActionCreator>;

export type PageEnterAndSubmitActionCreator = ReportActionCreatorWithProps<
   ReportActionTypes.PageEnterAndSubmit,
   ReportPageEnterActionProps & ReportParametersProp
>;

export type PageEnterAndSubmitAction = ReturnType<PageEnterAndSubmitActionCreator>;

export type PageExitActionCreator = ReportActionCreator<ReportActionTypes.PageExit>;
export type PageExitAction = ReturnType<PageEnterActionCreator>;

export type ReportParametersSubmittedActionCreator = ReportActionCreatorWithProps<
   ReportActionTypes.ReportParametersSubmitted,
   ReportParametersProp
>;
export type ReportParametersSubmittedAction = ReturnType<ReportParametersSubmittedActionCreator>;

export type ReportLoadSuccessActionCreator = ReportActionCreatorWithProps<
   ReportActionTypes.ReportLoadSuccess,
   ReportDataProp
>;
export type ReportLoadSuccessAction = ReturnType<ReportLoadSuccessActionCreator>;

export type ReportLoadFailureActionCreator = ReportActionCreatorWithProps<
   ReportActionTypes.ReportLoadFailure,
   ReportErrorProp
>;
export type ReportLoadFailureAction = ReturnType<ReportLoadFailureActionCreator>;

export type DownloadExcelActionCreator = ReportActionCreator<ReportActionTypes.DownloadExcel>;
export type DownloadExcelAction = ReturnType<DownloadPdfActionCreator>;

export type DownloadPdfActionCreator = ReportActionCreator<ReportActionTypes.DownloadPdf>;
export type DownloadPdfAction = ReturnType<DownloadPdfActionCreator>;

export type DownloadHtmlActionCreator = ReportActionCreator<ReportActionTypes.DownloadHtml>;
export type DownloadHtmlAction = ReturnType<DownloadHtmlActionCreator>;

export type DownloadSuccessActionCreator = ReportActionCreatorWithProps<
   ReportActionTypes.DownloadSuccess,
   ReportDownloadResultProp
>;
export type DownloadSuccessAction = ReturnType<DownloadSuccessActionCreator>;

export type DownloadFailureActionCreator = ReportActionCreatorWithProps<
   ReportActionTypes.DownloadFailure,
   ReportDownloadResultProp & ReportErrorProp
>;
export type DownloadFailureAction = ReturnType<DownloadFailureActionCreator>;

export type ReportParametersSaveActionCreator = ReportActionCreatorWithProps<
   ReportActionTypes.ReportParametersSave,
   ReportParametersSaveProp
>;

export type ReportParametersSaveSuccessActionCreator =
   ReportActionCreator<ReportActionTypes.ReportParametersSaveSuccess>;

export type ReportParametersSaveFailureActionCreator = ReportActionCreatorWithProps<
   ReportActionTypes.ReportParametersSaveFailure,
   ReportErrorProp
>;

export type ViewModeChangedActionCreator = ReportActionCreatorWithProps<
   ReportActionTypes.ReportViewModeChanged,
   ReportViewModeChangedActionProps
>;
export type ViewModeChangedAction = ReturnType<ViewModeChangedActionCreator>;

export enum ReportActionTypes {
   PageEnter = '[Report Page] Enter',
   DashboardPageEnter = '[Dashboard Page] Dashboard Page Enter',
   PageEnterDrilldown = '[Report Page] Drilldown Enter',
   PageEnterAndSubmit = '[Report Page] Enter and Submit',
   ReportParametersSubmitted = '[Report Page] Report Submit',
   ReportLoadSuccess = '[API] Report Load Success',
   ReportLoadFailure = '[API] Report Load Failure',
   DownloadExcel = '[Report Page] Download Excel',
   DownloadPdf = '[Report Page] Download PDF',
   DownloadHtml = '[Report Page] Download HTML',
   DownloadSuccess = '[API] Download Success',
   DownloadFailure = '[API] Download Failure',
   DrilldownReportFailure = '[Report Page] Drilldown Report Generation Failure',
   DrilldownParameterCheckSuccess = '[Report Page] Drilldown Parameter Check Success',
   FinalizeDrilldownReportParametersSuccess = '[Report Page] Drilldown Parameter Finalization Success',
   PageExit = '[Report Page] Exit',
   ReportParametersSave = '[Report Page] Parameters Save',
   ReportParametersSaveSuccess = '[API] Parameters Save Success',
   ReportParametersSaveFailure = '[API] Parameters Save Failure',
   ReportViewModeChanged = '[Report Page] View Mode Changed',
}

export const drilldownReportFailure = createAction(
   ReportActionTypes.DrilldownReportFailure,
   props<{ commonAndEnterReportProps: CommonReportActionProps & ReportPageEnterActionProps }>()
);

export const drilldownParameterCheckSuccess = createAction(
   ReportActionTypes.DrilldownParameterCheckSuccess,
   props<{
      commonAndEnterReportProps: CommonReportActionProps & ReportPageEnterActionProps;
      drilldownReportProps: ReportPageEnterDrilldownActionProps;
   }>()
);

export const drilldownParameterFinalizationSuccess = createAction(
   ReportActionTypes.FinalizeDrilldownReportParametersSuccess,
   props<{
      commonAndEnterReportProps: CommonReportActionProps & ReportPageEnterActionProps;
      drilldownReportProps: ReportPageEnterDrilldownActionProps;
      reportParameterExtraEntityInfo: string;
   }>()
);

export const pageEnter: PageEnterActionCreator = createAction(
   ReportActionTypes.PageEnter,
   props<CommonReportActionProps & ReportPageEnterActionProps>()
);

export const dashboardPageEnter = createAction(
   ReportActionTypes.DashboardPageEnter,
   props<{ tiles: ReportDashboardPageEnterActionProps[] }>()
);

export const pageEnterDrilldown = createAction(
   ReportActionTypes.PageEnterDrilldown,
   props<{
      drilldownReportProps: ReportPageEnterDrilldownActionProps;
      commonAndEnterReportProps: CommonReportActionProps & ReportPageEnterActionProps;
   }>()
);

export const viewModeChanged: ViewModeChangedActionCreator = createAction(
   ReportActionTypes.ReportViewModeChanged,
   props<CommonReportActionProps & ReportViewModeChangedActionProps>()
);

export const pageEnterAndSubmit: PageEnterAndSubmitActionCreator = createAction(
   ReportActionTypes.PageEnterAndSubmit,
   props<CommonReportActionProps & ReportPageEnterActionProps & ReportParametersProp>()
);

export const pageExit: PageExitActionCreator = createAction(
   ReportActionTypes.PageExit,
   props<CommonReportActionProps>()
);
export const reportParametersSubmitted: ReportParametersSubmittedActionCreator = createAction(
   ReportActionTypes.ReportParametersSubmitted,
   props<CommonReportActionProps & ReportParametersProp>()
);
export const reportLoadSuccess: ReportLoadSuccessActionCreator = createAction(
   ReportActionTypes.ReportLoadSuccess,
   props<CommonReportActionProps & ReportDataProp>()
);
export const reportLoadFailure: ReportLoadFailureActionCreator = createAction(
   ReportActionTypes.ReportLoadFailure,
   props<CommonReportActionProps & ReportErrorProp>()
);
export const downloadExcel: DownloadExcelActionCreator = createAction(
   ReportActionTypes.DownloadExcel,
   props<CommonReportActionProps>()
);
export const downloadPdf: DownloadPdfActionCreator = createAction(
   ReportActionTypes.DownloadPdf,
   props<CommonReportActionProps>()
);
export const downloadHtml: DownloadHtmlActionCreator = createAction(
   ReportActionTypes.DownloadHtml,
   props<CommonReportActionProps>()
);
export const downloadSuccess: DownloadSuccessActionCreator = createAction(
   ReportActionTypes.DownloadSuccess,
   props<CommonReportActionProps & ReportDownloadResultProp>()
);
export const downloadFailure: DownloadFailureActionCreator = createAction(
   ReportActionTypes.DownloadFailure,
   props<CommonReportActionProps & ReportDownloadResultProp & ReportErrorProp>()
);
export const reportParameterSave: ReportParametersSaveActionCreator = createAction(
   ReportActionTypes.ReportParametersSave,
   props<CommonReportActionProps & ReportParametersSaveProp>()
);
export const reportParameterSaveSuccess: ReportParametersSaveSuccessActionCreator = createAction(
   ReportActionTypes.ReportParametersSaveSuccess,
   props<CommonReportActionProps>()
);
export const reportParameterSaveFailure: ReportParametersSaveFailureActionCreator = createAction(
   ReportActionTypes.ReportParametersSaveFailure,
   props<CommonReportActionProps & ReportErrorProp>()
);
